"use client";
import { FormField } from "@/ui/form";
import {
  getMaxDateAllowedToReserve,
  getMinDateAllowedToReserve
} from "@/lib/utils";
import { SaveReservationData } from "@/features/reserve/utils/utils-reserve";
import { Office, ResourceType } from "@gettactic/api";
import { useMemo } from "react";
import { FieldErrors, UseFormReturn } from "react-hook-form";
import { CalendarInput } from "@cmp-src/form/calendar/CalendarInput/CalendarInput";
import { DateValue } from "@cmp-src/calendar/types";

type ReserveDateMultipleProps = {
  form: UseFormReturn<any>;
  weekDates: Date[];
  officeTimeZone: string;
  office?: Office;
  resType: ResourceType;
  errors: FieldErrors<SaveReservationData>;
  isAdmin?: boolean;
  extraDays?: number;
};

export function ReserveFieldDateMultiple({
  form,
  weekDates,
  officeTimeZone,
  office,
  resType,
  errors,
  isAdmin,
  extraDays
}: ReserveDateMultipleProps) {
  const minAllowedReserveDate = useMemo(
    () => getMinDateAllowedToReserve(officeTimeZone, weekDates),
    [officeTimeZone, weekDates]
  );
  const maxAllowedReserveDate = useMemo(
    () =>
      getMaxDateAllowedToReserve(officeTimeZone, office, resType, extraDays),
    [office, officeTimeZone, resType, extraDays]
  );

  const disabledDates = useMemo(() => {
    const matchers: Array<{ before: Date } | { after: Date }> = [];
    if (minAllowedReserveDate) {
      matchers.push({ before: minAllowedReserveDate });
    }
    if (maxAllowedReserveDate && !isAdmin) {
      matchers.push({ after: maxAllowedReserveDate });
    }
    return matchers.length > 0 ? matchers : undefined;
  }, [minAllowedReserveDate, maxAllowedReserveDate]);

  return (
    <>
      <FormField
        control={form.control}
        name="weekDates"
        render={({ field }) => (
          <CalendarInput
            mode="single"
            allowMultipleSwitch={true}
            setValueOnApply={true}
            hideCancel={true}
            hideSideContent={false}
            hideMultipleText={true}
            inputClassName="!border-none rounded-v2 bg-neutral-50 text-center font-mona font-normal"
            selectedDate={field.value?.length === 1 ? field.value[0] : field.value}
            showDayName={false}
            showDaysCount={true}
            onSelect={(date: DateValue | undefined) => {
              if (date instanceof Date) {
                form.setValue("weekDates", [date], {
                  shouldValidate: true
                });
              } else if (Array.isArray(date)) {
                form.setValue("weekDates", date, {
                  shouldValidate: true
                });
              }
            }}
            disabled={disabledDates}
            withArrows={false}
          />
        )}
      />
      {errors.weekDates ? (
        <p className="text-primary">Please choose a valid date</p>
      ) : null}
    </>
  );
}
